<template>
  <div class="scheduling-shell">
    <div class="border">
      <div class="header-row">
        <div class="header-text">
          <div class="pt-kicker">业务面板</div>
          <p class="card-title">{{ title }}</p>
        </div>
        <button class="close" @click="closeClick">&times;</button>
      </div>

      <div class="summary-grid">
        <div v-for="item in summaryCards" :key="item.label" class="summary-card">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.hint }}</em>
        </div>
      </div>

      <div v-if="sourceBanner" class="source-banner">
        <strong>{{ sourceBanner.title }}</strong>
        <span>{{ sourceBanner.detail }}</span>
      </div>

      <div class="controls">
        <div class="left-controls">
          <button class="btn btn-primary" @click="addTask">新增任务</button>
          <button class="btn" @click="refreshData">刷新</button>
          <button class="btn" @click="exportCSV">导出 CSV</button>
        </div>
        <div class="right-controls">
          <label>
            搜索：
            <input v-model="filterText" placeholder="按任务名 / 负责人 / 场景" />
          </label>
        </div>
      </div>

      <div class="content-grid">
        <div class="table-container">
          <table class="task-table">
            <thead>
            <tr>
              <th><input type="checkbox" v-model="selectAll" @change="toggleSelectAll" /></th>
              <th>任务ID</th>
              <th>任务名称</th>
              <th>负责人</th>
              <th>调度场景</th>
              <th>计划时段</th>
              <th>状态</th>
              <th>进度</th>
              <th>风险</th>
              <th>操作</th>
            </tr>
            </thead>
            <tbody>
            <tr
              v-for="task in pagedFiltered"
              :key="task.id"
              :class="{ active: selectedTask && selectedTask.id === task.id }"
              @click="openDetail(task)"
            >
              <td @click.stop><input type="checkbox" v-model="task._selected" /></td>
              <td>{{ task.id }}</td>
              <td class="task-name">{{ task.name }}</td>
              <td>{{ task.owner }}</td>
              <td>{{ task.scenario }}</td>
              <td>{{ task.start }} 至 {{ task.end }}</td>
              <td>
                <span :class="['status', statusClass(task.status)]">{{ task.status }}</span>
              </td>
              <td>
                <div class="progress-wrap">
                  <div class="progress-bar" :style="{ width: task.progress + '%' }">{{ task.progress }}%</div>
                </div>
              </td>
              <td>
                <span :class="['risk-pill', riskClass(task.riskLevel)]">{{ task.riskLevel }}</span>
              </td>
              <td class="actions" @click.stop>
                <button class="small" @click="openDetail(task)">详情</button>
                <button class="small" @click="runSimulation(task)">推演</button>
                <button class="small" @click="toggleRun(task)">{{ task.status === '运行中' ? '暂停' : '启动' }}</button>
                <button class="small" @click="editTask(task)">编辑</button>
              </td>
            </tr>

            <tr v-if="pagedFiltered.length === 0">
              <td colspan="10" class="no-data">没有符合条件的任务</td>
            </tr>
            </tbody>
          </table>
        </div>

        <aside v-if="selectedTask" class="detail-panel">
          <div class="detail-head">
            <div>
              <span class="detail-kicker">调度任务详情</span>
              <h3>{{ selectedTask.name }}</h3>
            </div>
            <span :class="['status', statusClass(selectedTask.status)]">{{ selectedTask.status }}</span>
          </div>

          <div class="detail-section">
            <h4>基础信息</h4>
            <div class="kv-grid">
              <p><span>任务编号</span><strong>{{ selectedTask.id }}</strong></p>
              <p><span>负责人</span><strong>{{ selectedTask.owner }}</strong></p>
              <p><span>调度场景</span><strong>{{ selectedTask.scenario }}</strong></p>
              <p><span>优先级</span><strong>{{ selectedTask.priority }}</strong></p>
            </div>
          </div>

          <div class="detail-section">
            <h4>当前水情边界</h4>
            <div class="metric-grid">
              <div v-for="m in selectedTask.metrics" :key="m.label" class="metric-card">
                <span>{{ m.label }}</span>
                <strong>{{ m.value }}</strong>
                <em>{{ m.trend }}</em>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>推荐调度方案</h4>
            <div class="plan-card">
              <div>
                <strong>{{ selectedTask.recommendation.name }}</strong>
                <p>{{ selectedTask.recommendation.summary }}</p>
              </div>
              <span :class="['risk-pill', riskClass(selectedTask.riskLevel)]">{{ selectedTask.riskLevel }}</span>
            </div>
            <ul class="plan-list">
              <li v-for="step in selectedTask.recommendation.steps" :key="step">{{ step }}</li>
            </ul>
          </div>

          <div class="detail-section">
            <h4>方案比选</h4>
            <div class="scheme-table">
              <div class="scheme-row scheme-head">
                <span>方案</span><span>最高水位</span><span>最大下泄</span><span>削峰率</span>
              </div>
              <div v-for="scheme in selectedTask.schemes" :key="scheme.name" class="scheme-row">
                <span>{{ scheme.name }}</span>
                <span>{{ scheme.maxLevel }} m</span>
                <span>{{ scheme.release }} m³/s</span>
                <span>{{ scheme.reduction }}%</span>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h4>调度过程线</h4>
            <div class="dispatch-timeline-head">
              <span>24小时滚动推演</span>
              <em>入库 / 下泄 / 库水位 / 库容占用</em>
            </div>
            <div ref="dispatchTimeline" class="dispatch-timeline"></div>
          </div>

          <div class="detail-section">
            <h4>调度指令记录</h4>
            <div class="log-list">
              <p v-for="log in selectedTask.logs" :key="log.time">
                <span>{{ log.time }}</span>{{ log.text }}
              </p>
            </div>
          </div>

          <div class="detail-actions">
            <button class="btn btn-primary" @click="runSimulation(selectedTask)">生成调度推演</button>
            <button class="btn" @click="toggleRun(selectedTask)">{{ selectedTask.status === '运行中' ? '暂停任务' : '启动任务' }}</button>
            <button class="btn" @click="selectedTask = null">收起详情</button>
          </div>
        </aside>
      </div>

      <div class="footer">
        <div class="pager">
          <button class="small" :disabled="page === 1" @click="page--">上一页</button>
          <span>第 {{ page }} / {{ totalPages }} 页</span>
          <button class="small" :disabled="page === totalPages" @click="page++">下一页</button>
        </div>
        <div class="batch-actions">
          <button class="btn" @click="batchPause">批量暂停</button>
          <button class="btn" @click="batchResume">批量启动</button>
          <button class="btn danger" @click="batchDelete">批量删除</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const TASK_TEMPLATES = [
  {
    name: '古贤水库防洪调度预演',
    owner: '张工',
    scenario: '20年一遇洪水',
    priority: '高',
    riskLevel: '较高',
    metrics: [
      { label: '当前库水位', value: '617.24 m', trend: '较1小时前 +0.12 m' },
      { label: '入库流量', value: '4280 m³/s', trend: '持续上涨' },
      { label: '下泄流量', value: '2260 m³/s', trend: '按规程递增' },
      { label: '可用防洪库容', value: '3.86 亿m³', trend: '满足预泄要求' },
    ],
    recommendation: {
      name: '均衡预泄调度',
      summary: '在不突破下游安全泄量的前提下提前预泄，兼顾防洪库容、生态基流与供水稳定。',
      steps: ['未来2小时下泄提升至2600 m³/s', '6小时内控制库水位不超过618.2 m', '滚动复核龙门断面洪峰并联动下游预警'],
    },
  },
  {
    name: '薛峰站水位联动调度',
    owner: '李工',
    scenario: '支流来水抬升',
    priority: '中',
    riskLevel: '中',
    metrics: [
      { label: '薛峰水位', value: '912.98 m', trend: '缓慢回落' },
      { label: '入库流量', value: '167 m³/s', trend: '小幅波动' },
      { label: '出库流量', value: '146 m³/s', trend: '稳定' },
      { label: '小时雨量', value: '22 mm/h', trend: '短历时增强' },
    ],
    recommendation: {
      name: '支流错峰调度',
      summary: '维持主库均衡出流，对薛峰站上游雨区进行滚动监测，避免支流洪峰与主河槽洪峰叠加。',
      steps: ['维持当前下泄146 m³/s', '30分钟刷新一次雨水情', '若雨强超过30 mm/h则启动黄色预警'],
    },
  },
  {
    name: '韩城供需保障调度',
    owner: '王工',
    scenario: '供水高峰',
    priority: '中',
    riskLevel: '低',
    metrics: [
      { label: '可供水量', value: '1260 万m³', trend: '满足计划' },
      { label: '综合需水', value: '1188 万m³', trend: '农业需求偏高' },
      { label: '生态基流', value: '186 m³/s', trend: '达标' },
      { label: '缺额预警', value: '0 万m³', trend: '暂无缺额' },
    ],
    recommendation: {
      name: '供水保障调度',
      summary: '保持生活与生态优先，农业灌溉按日计划错峰取水，供水保障率可维持在96%以上。',
      steps: ['韩城片区供水计划维持不变', '工业用水高峰向夜间错移', '每24小时复核供需平衡表'],
    },
  },
  {
    name: '张家卯水质风险联动',
    owner: '赵工',
    scenario: '雨后浊度升高',
    priority: '高',
    riskLevel: '中',
    metrics: [
      { label: '水质浊度', value: '22 NTU', trend: '较1小时前 +5.8' },
      { label: 'COD', value: '13.6 mg/L', trend: '正常' },
      { label: '氨氮', value: '0.31 mg/L', trend: '正常' },
      { label: '备用水源', value: '可启用', trend: '待命' },
    ],
    recommendation: {
      name: '水质保障调度',
      summary: '适度降低受影响断面取水比例，启用清水库调蓄，待浊度回落后恢复常态调度。',
      steps: ['水厂取水比例下调12%', '启用备用清水库2小时', '加密张家卯断面水质监测'],
    },
  },
]

export default {
  name: 'operationScheduling',
  props: {
    source: {
      type: String,
      default: ''
    },
    recommendedTask: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      title: '工程运行调度管理',
      filterText: '',
      page: 1,
      pageSize: 6,
      selectAll: false,
      selectedTask: null,
      tasks: this.generateMockTasks(14),
      chartResizeHandler: null,
    }
  },
  computed: {
    sourceBanner() {
      if (this.source === 'supplyDemand') {
        return {
          title: '供需缺口联动调度',
          detail: this.recommendedTask || '已根据供需平衡结果自动定位到供水保障调度任务，建议优先查看推荐方案和24小时调度过程线。'
        }
      }
      return null
    },
    filtered() {
      const q = this.filterText.trim().toLowerCase()
      if (!q) return this.tasks
      return this.tasks.filter(t => `${t.name} ${t.owner} ${t.scenario}`.toLowerCase().includes(q))
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filtered.length / this.pageSize))
    },
    pagedFiltered() {
      if (this.page > this.totalPages) this.page = this.totalPages
      const start = (this.page - 1) * this.pageSize
      return this.filtered.slice(start, start + this.pageSize)
    },
    summaryCards() {
      const running = this.tasks.filter(t => t.status === '运行中').length
      const highRisk = this.tasks.filter(t => t.riskLevel === '较高' || t.riskLevel === '高').length
      const avgProgress = Math.round(this.tasks.reduce((sum, t) => sum + t.progress, 0) / this.tasks.length)
      return [
        { label: '运行任务', value: `${running} 项`, hint: '滚动调度中' },
        { label: '高关注风险', value: `${highRisk} 项`, hint: '需会商复核' },
        { label: '平均进度', value: `${avgProgress}%`, hint: '调度闭环完成度' },
        { label: '今日指令', value: '18 条', hint: '含预泄/供水/水质' },
      ]
    },
  },
  mounted() {
    this.selectedTask = this.source === 'supplyDemand'
      ? (this.tasks.find(task => /渚涢渶|供需|供水/.test(task.name + task.scenario)) || this.tasks[0])
      : this.tasks[0]
    this.chartResizeHandler = () => {
      if (this._dispatchChart) this._dispatchChart.resize()
    }
    window.addEventListener('resize', this.chartResizeHandler)
    this.$nextTick(this.renderDispatchTimeline)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.chartResizeHandler)
    if (this._dispatchChart) {
      this._dispatchChart.dispose()
      this._dispatchChart = null
    }
  },
  methods: {
    closeClick() {
      this.$emit('infoClose')
    },
    generateMockTasks(n) {
      const statuses = ['已停止', '运行中', '待执行']
      const list = []
      for (let i = 1; i <= n; i++) {
        const template = TASK_TEMPLATES[(i - 1) % TASK_TEMPLATES.length]
        const progress = [17, 50, 70, 86, 77, 84, 34, 61, 45, 92, 28, 73, 56, 39][i - 1] || 40
        const status = statuses[i % statuses.length]
        list.push(this.buildTask(`T${1000 + i}`, template, i, progress, status))
      }
      return list
    },
    buildTask(id, template, index, progress, status) {
      const schemes = this.buildSchemes(index)
      return {
        id,
        name: index <= TASK_TEMPLATES.length ? template.name : `${template.name}-${Math.ceil(index / TASK_TEMPLATES.length)}`,
        owner: template.owner,
        start: this.fakeDate(-5 + index),
        end: this.fakeDate(1 + index),
        status,
        progress,
        priority: template.priority,
        scenario: template.scenario,
        riskLevel: template.riskLevel,
        metrics: template.metrics,
        recommendation: template.recommendation,
        schemes,
        timeline: this.buildTimeline(index),
        logs: this.buildLogs(template),
        _selected: false,
      }
    },
    buildSchemes(index) {
      const baseLevel = 617.6 + (index % 4) * 0.18
      const baseRelease = 2450 + (index % 5) * 180
      return [
        { name: '保守调度', maxLevel: (baseLevel + 0.82).toFixed(2), release: baseRelease - 260, reduction: 18 + (index % 4) },
        { name: '均衡调度', maxLevel: (baseLevel + 0.36).toFixed(2), release: baseRelease, reduction: 27 + (index % 5) },
        { name: '泄洪优先', maxLevel: (baseLevel - 0.12).toFixed(2), release: baseRelease + 420, reduction: 34 + (index % 6) },
      ]
    },
    buildTimeline(index) {
      const offset = index % 5
      const time = ['0h', '2h', '4h', '6h', '8h', '10h', '12h', '16h', '20h', '24h']
      const inflowBase = [1180, 1760, 2580, 3520, 4360, 4820, 4050, 2960, 2040, 1380]
      const releaseBase = [930, 1210, 1540, 1880, 2160, 2290, 2080, 1740, 1360, 1080]
      return {
        time,
        inflow: inflowBase.map((v, i) => v + offset * 90 + (i % 3) * 35),
        release: releaseBase.map((v, i) => v + offset * 70 + (i % 2) * 30),
        level: [616.10, 616.28, 616.58, 616.93, 617.26, 617.42, 617.24, 616.91, 616.56, 616.30].map(v => Number((v + offset * 0.06).toFixed(2))),
        storage: [62, 65, 69, 73, 78, 81, 77, 71, 66, 63].map(v => v + offset),
      }
    },
    buildLogs(template) {
      return [
        { time: '08:30', text: `完成${template.scenario}边界条件复核。` },
        { time: '09:10', text: `调度中心建议采用${template.recommendation.name}。` },
        { time: '10:00', text: '已同步下游责任单位和监测站点。' },
      ]
    },
    fakeDate(offsetDays = 0) {
      const d = new Date()
      d.setDate(d.getDate() + offsetDays)
      return d.toISOString().slice(0, 10)
    },
    openDetail(task) {
      this.selectedTask = task
      this.$nextTick(this.renderDispatchTimeline)
    },
    refreshData() {
      this.tasks.forEach((t, index) => {
        if (t.status === '运行中') {
          t.progress = Math.min(100, t.progress + 4 + (index % 6))
          if (t.progress >= 100) t.status = '已完成'
        }
      })
      if (this.$message) this.$message.success('调度任务已刷新，水情和指令状态已滚动更新。')
    },
    addTask() {
      const template = TASK_TEMPLATES[0]
      const id = `T${1000 + this.tasks.length + 1}`
      const task = this.buildTask(id, template, this.tasks.length + 1, 0, '待执行')
      task.name = '古贤水库临时会商调度'
      task.owner = '调度中心'
      task.scenario = '临时暴雨过程'
      this.tasks.unshift(task)
      this.selectedTask = task
      this.page = 1
      this.$nextTick(this.renderDispatchTimeline)
      if (this.$message) this.$message.success('已生成一条临时调度任务，并填充默认演示工况。')
    },
    editTask(task) {
      task.priority = task.priority === '高' ? '中' : '高'
      task.logs.unshift({ time: '当前', text: `已调整任务优先级为${task.priority}。` })
      this.selectedTask = task
      this.$nextTick(this.renderDispatchTimeline)
      if (this.$message) this.$message.success('任务已模拟编辑，可在详情中查看更新记录。')
    },
    deleteTask(task) {
      this.tasks = this.tasks.filter(t => t !== task)
      if (this.selectedTask && this.selectedTask.id === task.id) this.selectedTask = this.tasks[0] || null
      if (this.$message) this.$message.success(`已删除 ${task.id}。`)
    },
    toggleRun(task) {
      if (task.status === '运行中') {
        task.status = '已停止'
        task.logs.unshift({ time: '当前', text: '任务已暂停，保持当前闸门开度和监测频率。' })
      } else {
        task.status = '运行中'
        task.logs.unshift({ time: '当前', text: '任务已启动，调度指令进入滚动执行状态。' })
      }
      this.selectedTask = task
      this.$nextTick(this.renderDispatchTimeline)
    },
    runSimulation(task) {
      task.status = '运行中'
      task.progress = Math.min(100, task.progress + 12)
      task.logs.unshift({ time: '当前', text: `已完成${task.recommendation.name}推演，推荐方案已更新至详情面板。` })
      this.selectedTask = task
      this.$nextTick(this.renderDispatchTimeline)
      if (this.$message) {
        const best = task.schemes[1]
        this.$message.success(`推演完成：推荐${task.recommendation.name}，最高水位 ${best.maxLevel} m，削峰率 ${best.reduction}%。`)
      }
    },
    renderDispatchTimeline() {
      if (!this.selectedTask || !this.selectedTask.timeline || !this.$refs.dispatchTimeline || !this.$echarts) return
      if (this._dispatchChart && this._dispatchChart.getDom && this._dispatchChart.getDom() !== this.$refs.dispatchTimeline) {
        this._dispatchChart.dispose()
        this._dispatchChart = null
      }
      if (!this._dispatchChart) {
        this._dispatchChart = this.$echarts.init(this.$refs.dispatchTimeline)
      }
      const timeline = this.selectedTask.timeline
      this._dispatchChart.setOption({
        color: ['#7ee6ff', '#ffd36a', '#7dffbc', '#b78cff'],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(2, 22, 30, 0.94)',
          borderColor: 'rgba(126, 230, 255, 0.35)',
          textStyle: { color: '#eaffff' }
        },
        legend: {
          top: 0,
          textStyle: { color: 'rgba(230, 247, 251, 0.82)', fontSize: 10 },
          data: ['入库流量', '下泄流量', '库水位', '库容占用']
        },
        grid: { left: 42, right: 58, top: 32, bottom: 24 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: timeline.time,
          axisLine: { lineStyle: { color: 'rgba(230, 247, 251, 0.25)' } },
          axisLabel: { color: 'rgba(230, 247, 251, 0.76)', fontSize: 10 }
        },
        yAxis: [
          {
            type: 'value',
            name: 'm³/s',
            min: 0,
            axisLine: { show: false },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.06)' } },
            axisLabel: { color: 'rgba(230, 247, 251, 0.72)', fontSize: 10 }
          },
          {
            type: 'value',
            name: 'm',
            min: 615.5,
            max: 618.5,
            axisLine: { show: false },
            splitLine: { show: false },
            axisLabel: { color: 'rgba(230, 247, 251, 0.72)', fontSize: 10 }
          },
          {
            type: 'value',
            name: '%',
            min: 50,
            max: 90,
            offset: 36,
            axisLine: { show: false },
            splitLine: { show: false },
            axisLabel: { color: 'rgba(230, 247, 251, 0.72)', fontSize: 10 }
          }
        ],
        series: [
          { name: '入库流量', type: 'line', smooth: true, data: timeline.inflow, symbolSize: 5, areaStyle: { opacity: 0.08 } },
          { name: '下泄流量', type: 'line', smooth: true, data: timeline.release, symbolSize: 5 },
          { name: '库水位', type: 'line', smooth: true, yAxisIndex: 1, data: timeline.level, symbolSize: 5 },
          { name: '库容占用', type: 'line', smooth: true, yAxisIndex: 2, data: timeline.storage, symbolSize: 5 }
        ]
      })
      this._dispatchChart.resize()
    },
    statusClass(s) {
      if (s === '运行中') return 'status-running'
      if (s === '已完成') return 'status-done'
      if (s === '已停止') return 'status-stopped'
      return 'status-pending'
    },
    riskClass(s) {
      if (s === '较高' || s === '高') return 'risk-high'
      if (s === '中') return 'risk-mid'
      return 'risk-low'
    },
    toggleSelectAll() {
      this.pagedFiltered.forEach(t => {
        t._selected = this.selectAll
      })
    },
    batchPause() {
      const selected = this.tasks.filter(t => t._selected)
      selected.forEach(t => {
        t.status = '已停止'
        t.logs.unshift({ time: '当前', text: '批量暂停任务。' })
      })
      this.clearSelections()
    },
    batchResume() {
      const selected = this.tasks.filter(t => t._selected)
      selected.forEach(t => {
        t.status = '运行中'
        t.logs.unshift({ time: '当前', text: '批量启动任务。' })
      })
      this.clearSelections()
    },
    batchDelete() {
      const selectedIds = this.tasks.filter(t => t._selected).map(t => t.id)
      this.tasks = this.tasks.filter(t => !t._selected)
      if (this.selectedTask && selectedIds.includes(this.selectedTask.id)) this.selectedTask = this.tasks[0] || null
      this.clearSelections()
    },
    clearSelections() {
      this.tasks.forEach(t => (t._selected = false))
      this.selectAll = false
    },
    exportCSV() {
      const rows = [
        ['任务ID', '任务名称', '负责人', '调度场景', '计划开始', '计划结束', '状态', '进度', '风险', '推荐方案'],
      ]
      this.tasks.forEach(t => {
        rows.push([t.id, t.name, t.owner, t.scenario, t.start, t.end, t.status, `${t.progress}%`, t.riskLevel, t.recommendation.name])
      })
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'guxian_scheduling_export.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.scheduling-shell {
  width: min(1180px, calc(100vw - 230px));
  height: calc(100vh - 150px);
  position: absolute;
  left: 50%;
  top: 112px;
  transform: translateX(-54%);
  z-index: 9998;
}

.border {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(8, 18, 32, 0.96), rgba(13, 38, 50, 0.94));
  border: 1px solid rgba(99, 179, 237, 0.28);
  border-radius: 10px;
  padding: 18px;
  box-sizing: border-box;
  color: #eef7ff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 18px 52px rgba(0, 0, 0, 0.42);
}

.header-row,
.controls,
.footer,
.detail-head,
.detail-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-row {
  gap: 16px;
  margin-bottom: 14px;
}

.header-text {
  flex: 1;
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}

.close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, border-color 0.2s;
}
.close:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.28);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0;
}

.summary-card,
.metric-card,
.plan-card {
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 10px;
}

.summary-card span,
.metric-card span,
.kv-grid span,
.detail-kicker {
  display: block;
  color: rgba(255, 255, 255, 0.52);
  font-size: 12px;
}

.summary-card strong,
.metric-card strong {
  display: block;
  margin: 4px 0;
  color: #fff;
  font-size: 19px;
}

.summary-card em,
.metric-card em {
  color: rgba(125, 211, 252, 0.76);
  font-size: 12px;
  font-style: normal;
}

.source-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: 0 0 10px;
  padding: 10px 12px;
  border: 1px solid rgba(74, 158, 255, 0.28);
  border-radius: 6px;
  background: rgba(74, 158, 255, 0.11);
}

.source-banner strong {
  flex: 0 0 auto;
  color: #8bd4ff;
  font-size: 13px;
}

.source-banner span {
  flex: 1;
  color: rgba(238, 247, 255, 0.72);
  font-size: 12px;
  line-height: 1.45;
}

.controls {
  margin-bottom: 10px;
}

.left-controls .btn {
  margin-right: 8px;
}

.right-controls input {
  width: 190px;
  margin-left: 6px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 5px;
  color: #fff;
  padding: 6px 8px;
  outline: none;
}

.btn,
.small {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #dffcff;
  border-radius: 5px;
  cursor: pointer;
}

.btn {
  padding: 6px 10px;
}

.small {
  padding: 4px 7px;
  font-size: 12px;
}

.btn-primary {
  background: rgba(56, 189, 248, 0.12);
  color: #b8ecff;
  border: 1px solid rgba(56, 189, 248, 0.32);
}

.btn.danger {
  background: rgba(255, 80, 80, 0.12);
  color: #ffb3b3;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) 360px;
  gap: 12px;
  min-height: 0;
  flex: 1;
}

.table-container,
.detail-panel {
  overflow: auto;
  background: rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 8px;
}

.task-table {
  width: 100%;
  border-collapse: collapse;
  color: rgba(255, 255, 255, 0.72);
}

.task-table th,
.task-table td {
  padding: 8px 9px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  text-align: left;
  font-size: 12px;
  white-space: nowrap;
}

.task-table thead th {
  background: rgba(0, 0, 0, 0.22);
  position: sticky;
  top: 0;
  z-index: 2;
}

.task-table tbody tr {
  cursor: pointer;
}

.task-table tbody tr:hover,
.task-table tbody tr.active {
  background: rgba(56, 189, 248, 0.06);
}

.task-name {
  font-weight: 600;
  color: #bff7ff;
}

.status,
.risk-pill {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
}

.status-running {
  background: rgba(0, 200, 120, 0.14);
  color: #9fffd6;
}

.status-stopped {
  background: rgba(255, 60, 60, 0.1);
  color: #ffd3d3;
}

.status-pending {
  background: rgba(255, 200, 0, 0.08);
  color: #fff6c2;
}

.status-done {
  background: rgba(68, 158, 255, 0.14);
  color: #b8dcff;
}

.risk-high {
  background: rgba(255, 96, 64, 0.16);
  color: #ffc1b4;
}

.risk-mid {
  background: rgba(255, 196, 67, 0.16);
  color: #ffe29b;
}

.risk-low {
  background: rgba(0, 210, 150, 0.14);
  color: #a7ffdf;
}

.progress-wrap {
  width: 110px;
  height: 18px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  line-height: 18px;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  background: linear-gradient(90deg, rgba(56, 189, 248, 0.25), rgba(56, 189, 248, 0.58));
  color: #002a30;
}

.actions {
  display: flex;
  gap: 5px;
}

.detail-panel {
  border: 1px solid rgba(99, 179, 237, 0.28);
}

.detail-head h3 {
  margin: 3px 0 0;
  color: #fff;
  font-size: 17px;
}

.detail-section {
  margin-top: 12px;
}

.detail-section h4 {
  margin: 0 0 8px;
  color: #76d9ff;
  font-size: 14px;
}

.kv-grid,
.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.kv-grid p {
  margin: 0;
  padding: 8px;
  background: rgba(255, 255, 255, 0.045);
  border-radius: 6px;
}

.kv-grid strong {
  display: block;
  color: #fff;
  margin-top: 3px;
}

.plan-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.plan-card strong {
  color: #fff;
}

.plan-card p {
  margin: 5px 0 0;
  color: rgba(230, 247, 251, 0.78);
  line-height: 1.5;
}

.plan-list {
  margin: 8px 0 0;
  padding-left: 18px;
  color: rgba(230, 247, 251, 0.86);
  line-height: 1.7;
}

.scheme-table,
.log-list {
  background: rgba(255, 255, 255, 0.045);
  border-radius: 6px;
  overflow: hidden;
}

.scheme-row {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 0.9fr 0.7fr;
  gap: 8px;
  padding: 7px 9px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
  font-size: 12px;
}

.scheme-head {
  color: #9ceff2;
  background: rgba(0, 0, 0, 0.16);
}

.dispatch-timeline-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 9px;
  background: rgba(255, 255, 255, 0.045);
  border-radius: 6px 6px 0 0;
  color: #9ceff2;
  font-size: 12px;
}

.dispatch-timeline-head em {
  color: rgba(230, 247, 251, 0.62);
  font-style: normal;
}

.dispatch-timeline {
  height: 220px;
  width: 100%;
  background: rgba(255, 255, 255, 0.035);
  border-radius: 0 0 6px 6px;
}

.log-list p {
  margin: 0;
  padding: 8px 9px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.045);
  color: rgba(230, 247, 251, 0.84);
  line-height: 1.45;
}

.log-list span {
  color: #9ceff2;
  margin-right: 8px;
}

.detail-actions {
  gap: 8px;
  justify-content: flex-start;
  margin-top: 12px;
}

.footer {
  margin-top: 8px;
}

.pager span {
  margin: 0 8px;
  color: #cfeff3;
}

.no-data {
  text-align: center;
  padding: 18px;
  color: #cfeff3;
}

@media (max-width: 1200px) {
  .scheduling-shell {
    width: 86%;
    left: 7%;
  }
  .content-grid {
    grid-template-columns: 1fr;
  }
  .detail-panel {
    max-height: 360px;
  }
}
</style>
